import type { AssistantTableSpec } from "@/types/account";

type ParsedTable = {
  start: number;
  end: number;
  title: string;
  columns: { key: string; label: string }[];
  rows: Record<string, unknown>[];
};

const SEP_CELL = /^:?-{3,}:?$/;
const BOILERPLATE_LINE =
  /^\s*(you can (reopen the table|view the details below)|if you need anything else)\b.*$/gim;

function splitRow(line: string): string[] {
  let t = String(line || "").trim();
  if (!t) return [];
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|")) t = t.slice(0, -1);
  return t.split("|").map((c) => c.trim());
}

function isSeparatorRow(line: string): boolean {
  const cells = splitRow(line);
  if (cells.length < 2) return false;
  return cells.every((c) => SEP_CELL.test(c.replace(/\s/g, "")));
}

function isPipeRow(line: string): boolean {
  const t = String(line || "").trim();
  if (!t.includes("|")) return false;
  if (isSeparatorRow(t)) return true;
  return splitRow(t).length >= 2;
}

function colKey(label: string, index: number, used: Set<string>): string {
  const base =
    String(label || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "") || `col_${index + 1}`;
  let key = base;
  let n = 2;
  while (used.has(key)) {
    key = `${base}_${n++}`;
  }
  used.add(key);
  return key;
}

function capFirst(s: string): string {
  const t = s.trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function inferTitle(preceding: string, columns: { label: string }[], index: number): string {
  const lines = String(preceding || "")
    .split(/\n+/)
    .map((l) => l.replace(/^#{1,6}\s+/, "").trim())
    .filter(Boolean);
  for (let i = lines.length - 1; i >= 0; i--) {
    let line = lines[i].replace(/[:.]\s*$/, "");
    if (BOILERPLATE_LINE.test(line)) continue;
    line = line.replace(/^(here(?:'s| is| are)\s+)/i, "");
    line = line.replace(/^(i(?:['’]?ve| have) compiled\s+)/i, "");
    line = line.replace(/\s+into a table\b.*$/i, "");
    line = line.replace(/\s+along with their urls and titles$/i, "");
    line = line.replace(/\s+in the (table|chat)\b.*$/i, "");
    if (line.length >= 4 && line.length <= 90) return capFirst(line);
  }
  if (columns.length) {
    const labels = columns.map((c) => c.label).filter(Boolean);
    if (labels.length) return labels.slice(0, 3).join(" · ");
  }
  return index === 0 ? "View table" : `View table ${index + 1}`;
}

function parseTableFromLines(
  lines: string[],
  headerIdx: number
): { consumed: number; columns: { key: string; label: string }[]; rows: Record<string, unknown>[] } | null {
  const header = splitRow(lines[headerIdx] || "");
  const sep = lines[headerIdx + 1];
  if (header.length < 2 || !isSeparatorRow(sep || "")) return null;
  const used = new Set<string>();
  const columns = header.map((label, i) => ({ key: colKey(label, i, used), label: label || `Column ${i + 1}` }));
  const rows: Record<string, unknown>[] = [];
  let i = headerIdx + 2;
  while (i < lines.length && isPipeRow(lines[i] || "")) {
    if (isSeparatorRow(lines[i] || "")) {
      i += 1;
      continue;
    }
    const cells = splitRow(lines[i] || "");
    if (!cells.some((c) => c)) {
      i += 1;
      continue;
    }
    const row: Record<string, unknown> = {};
    columns.forEach((c, ci) => {
      row[c.key] = cells[ci] ?? "";
    });
    rows.push(row);
    i += 1;
  }
  return { consumed: i - headerIdx, columns, rows };
}

function lineOffsets(text: string): { line: string; start: number; end: number }[] {
  const lines = String(text || "").split("\n");
  const out: { line: string; start: number; end: number }[] = [];
  let pos = 0;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    out.push({ line, start: pos, end: pos + line.length });
    pos += line.length + (i < lines.length - 1 ? 1 : 0);
  }
  return out;
}

function parseMarkdownTables(text: string): ParsedTable[] {
  const raw = String(text || "");
  if (!raw.includes("|")) return [];
  const rows = lineOffsets(raw);
  const tables: ParsedTable[] = [];
  let i = 0;
  let tableIndex = 0;
  while (i < rows.length) {
    const trimmed = rows[i].line.trim();
    if (trimmed.startsWith("```")) {
      let j = i + 1;
      while (j < rows.length && !rows[j].line.trim().startsWith("```")) j += 1;
      const inner = rows.slice(i + 1, j).map((r) => r.line);
      let parsed: ReturnType<typeof parseTableFromLines> = null;
      for (let k = 0; k < inner.length - 1 && !parsed; k++) {
        parsed = parseTableFromLines(inner, k);
      }
      if (parsed && parsed.columns.length >= 2) {
        const preceding = raw.slice(0, rows[i].start);
        tables.push({
          start: rows[i].start,
          end: (rows[Math.min(j, rows.length - 1)] || rows[i]).end,
          title: inferTitle(preceding, parsed.columns, tableIndex),
          columns: parsed.columns,
          rows: parsed.rows
        });
        tableIndex += 1;
      }
      i = j + 1;
      continue;
    }
    if (i + 1 < rows.length && isPipeRow(rows[i].line) && isSeparatorRow(rows[i + 1].line)) {
      const parsed = parseTableFromLines(
        rows.map((r) => r.line),
        i
      );
      if (parsed && parsed.columns.length >= 2) {
        const last = i + parsed.consumed - 1;
        tables.push({
          start: rows[i].start,
          end: rows[last]?.end ?? rows[i].end,
          title: inferTitle(raw.slice(0, rows[i].start), parsed.columns, tableIndex),
          columns: parsed.columns,
          rows: parsed.rows
        });
        tableIndex += 1;
        i += parsed.consumed;
        continue;
      }
    }
    i += 1;
  }
  return tables;
}

function stripBoilerplate(text: string): string {
  return String(text || "")
    .replace(/\s*you can view the details below\.?/gi, "")
    .replace(/\s*you can reopen the table[^.!\n]*[.!]?/gi, "")
    .replace(/\s*if you need anything else[^.!\n]*[.!]?/gi, "")
    .replace(BOILERPLATE_LINE, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const INTRO_ONLY =
  /^(here(?:'s| is| are)|i(?:['’]?ve| have) (?:compiled|put|listed)|below is|the following|you can view)\b[\s\S]{0,220}$/i;

function hideIntroIfOnlyTable(text: string, hasTables: boolean): string {
  if (!hasTables) return text;
  const compact = text.replace(/\s+/g, " ").trim();
  if (!compact) return "";
  if (INTRO_ONLY.test(compact)) return "";
  return text;
}

function hideTrailingPartialTable(text: string): string {
  let raw = String(text || "");
  const fence = raw.lastIndexOf("```");
  if (fence >= 0 && !raw.slice(fence + 3).includes("```")) {
    raw = raw.slice(0, fence).replace(/\s+$/, "");
  }
  const lines = raw.split("\n");
  let i = lines.length - 1;
  while (i >= 0 && !String(lines[i] || "").trim()) i -= 1;
  if (i < 0 || !isPipeRow(lines[i] || "")) return raw;
  let start = i;
  while (start >= 0 && (isPipeRow(lines[start] || "") || !String(lines[start] || "").trim())) start -= 1;
  start += 1;
  if (start >= lines.length) return raw;
  return lines.slice(0, start).join("\n").replace(/\s+$/, "");
}

function asTableSpec(raw: unknown, index: number): AssistantTableSpec | null {
  if (!raw || typeof raw !== "object") return null;
  const t = raw as AssistantTableSpec;
  const columns = Array.isArray(t.columns) ? t.columns.filter((c) => c && (c.key || c.label)) : [];
  const rows = Array.isArray(t.rows) ? t.rows.filter((r) => r && typeof r === "object") : [];
  if (!columns.length && !rows.length) return null;
  return {
    tableId: t.tableId || `md_${index + 1}`,
    title: String(t.title || "").trim() || (index === 0 ? "View table" : `View table ${index + 1}`),
    subtitle: t.subtitle,
    columns: columns.map((c, i) => ({
      key: String(c.key || `col_${i + 1}`),
      label: String(c.label || c.key || `Column ${i + 1}`)
    })),
    rows
  };
}

function signature(spec: AssistantTableSpec): string {
  const cols = (spec.columns || []).map((c) => String(c.label || c.key || "").toLowerCase()).join("|");
  const preview = (spec.rows || [])
    .slice(0, 3)
    .map((r) =>
      (spec.columns || [])
        .map((c) => String(r[c.key] ?? "").trim().toLowerCase())
        .join("|")
    )
    .join(";");
  return `${cols}::${preview}`;
}

export function hydrateAssistantMessage(
  content: string,
  existingTables?: unknown[],
  opts?: { hidePartial?: boolean }
): { content: string; tables: AssistantTableSpec[] } {
  const source = opts?.hidePartial ? hideTrailingPartialTable(content) : String(content || "");
  const parsed = parseMarkdownTables(source);
  let next = source;
  for (let i = parsed.length - 1; i >= 0; i--) {
    const t = parsed[i];
    next = `${next.slice(0, t.start)}${next.slice(t.end)}`;
  }

  const tables: AssistantTableSpec[] = [];
  const seen = new Set<string>();
  const push = (spec: AssistantTableSpec | null) => {
    if (!spec) return;
    const sig = signature(spec);
    if (seen.has(sig)) return;
    seen.add(sig);
    tables.push(spec);
  };

  (Array.isArray(existingTables) ? existingTables : []).forEach((t, i) => push(asTableSpec(t, i)));
  parsed.forEach((t, i) => {
    if (!t.rows.length) return;
    push({
      tableId: `md_${i + 1}`,
      title: t.title,
      columns: t.columns,
      rows: t.rows
    });
  });

  next = hideIntroIfOnlyTable(stripBoilerplate(next), tables.length > 0);
  return { content: next, tables };
}
